<template>
  <div class="content">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Stocktaking ({{ dateFormat(stocktaking.date) }})</h1>
          <div class="status-title" :class="[`status-title--${stocktaking.status.code}`]">
            {{ stocktaking.status.name }}
          </div>
        </app-cells>
        <app-button
          @click.native="onShowModal('delete')"
          v-if="stocktaking.status.code === 'draft' && showControls"
          theme="error"
        >
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Value increase:</span><span>{{ stocktaking.products_increase.toLocaleString() }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Value decrease:</span><span>{{ stocktaking.products_decrease.toLocaleString() }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Balance:</span><span>{{ stocktaking.products_balance.toLocaleString() }} TSh</span>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Storekeeper:</span>
              <span v-if="stocktaking.storekeeper.first_name">{{ stocktaking.storekeeper.first_name }} {{ stocktaking.storekeeper.last_name }}</span>
              <span v-else>-</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Supervisor:</span>
              <span v-if="stocktaking.supervisor.first_name">{{ stocktaking.supervisor.first_name }} {{ stocktaking.supervisor.last_name }}</span>
              <span v-else>-</span>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="stocktaking.note"
          rows="5"
          @change.native="onUpdateNotes"
          placeholder="Write a notes"
          :readonly="stocktaking.status.code === 'completed'"
        />
      </app-cells>
      <app-cells>
        <app-input
          @input.native="onOrdersSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search by product name"
          name="search"
        />
        <app-checkbox
          v-model="zero_balance"
          @change="onChangeFilter"
          label="Hide zero balance"
        />
        <app-checkbox
          v-model="zero_difference"
          @change="onChangeFilter"
          label="Hide zero difference"
        />
      </app-cells>
      <div v-for="category of products" :key="category.id" class="stocktaking-block">
        <app-cells>
          <h2 class="title">{{ category.name }}</h2>
        </app-cells>
        <v-client-table
          :data="category.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
        >
          <template #h__product.name>
            <div class="table-default__left">Product name</div>
          </template>
          <template #count="{ row, index }">
            {{ index }}
          </template>
          <template #product.name="{ row }">
            <div class="table-default__left">
              <router-link :to="{name: 'product-storage-products-detail', params: {id: row.product.id }}" class="table-default__link-border">
                {{ row.product.name }}
              </router-link>
            </div>
          </template>
          <template #real_stock="{ row }">
            <app-cells v-if="stocktaking.status.code === 'draft'" position="center" :indent="false">
              <input
                v-model.number="row.real_stock"
                class="input input--300"
                type="number"
                @change="onChangeTableInput(row.id, row.real_stock)"
                style="text-align: center"
              />
            </app-cells>
            <template v-else>{{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 1}).format(row.real_stock) }}</template>
          </template>
          <template #current_stock="{ row }">
            {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 1}).format(row.current_stock) }}
          </template>
          <template #difference_stock="{ row }">
            <span
              :class="{
                'table-default__text-green': row.difference_stock > 0,
                'table-default__text-red': row.difference_stock < 0,
              }"
            >
              <span v-if="row.difference_stock > 0">+</span>
              {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 1}).format(row.difference_stock) }}
            </span>
          </template>
          <template #difference_value="{ row }">
            <span
            :class="{
              'table-default__text-green': row.difference_value > 0,
              'table-default__text-red': row.difference_value < 0,
              }"
            >
              <span v-if="row.difference_value > 0">+</span>{{ row.difference_value.toLocaleString() }} TSh
            </span>
          </template>
        </v-client-table>
      </div>
      <app-cells position="end">
        <app-button @click.native="onPrint" theme="warn" ref="print">
          Print stocktaking
        </app-button>
        <app-button v-if="stocktaking.status.code === 'draft' && showControls" @click.native="onShowModal('update')">
          Update Inventory
        </app-button>
      </app-cells>
    </template>
    <modal
      name="control-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('control-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          <span v-if="modal_type === 'delete'">
            Delete stocktaking
          </span>
          <span v-else>
            Update inventory
          </span>
        </div>
        <div class="popup__body">
          <div class="popup__subt">
            <span v-if="modal_type === 'delete'">
              Are you sure you want to delete this stocktaking?
            </span>
            <span v-else>
              Are you sure you want to do a full inventory? The product balance will change and cannot be undone
            </span>
          </div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideModal" theme="dark">Cancel</app-button>
            <app-button v-if="modal_type === 'delete'" @click.native="onDeleteItem" ref="load">Yes</app-button>
            <app-button v-else @click.native="onUpdateInventory" ref="load">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteProductStorageStocktaking,
  getProductStorageStocktakingDetail,
  getProductStorageStocktakingProducts,
  getStocktakingPrint,
  patchProductStorageStocktaking,
  patchProductStorageStocktakingProduct,
} from '@/http'
import { format, parse } from 'date-fns'
import debounce from 'lodash/debounce'

export default {
  name: 'ProductStorageStocktakingDetail',
  data() {
    return {
      search: '',
      is_loaded: false,
      stocktaking: {},
      zero_balance: true,
      zero_difference: false,
      products: [],
      table: {
        columns: ['count', 'product.name', 'real_stock', 'current_stock', 'product.measurement.name', 'difference_stock', 'difference_value'],
        options: {
          headings: {
            'count': '#',
            'product.name': 'Product name',
            'real_stock': 'Real qty',
            'current_stock': 'Declared qty',
            'product.measurement.name': 'Unit',
            'difference_stock': 'Qty difference',
            'difference_value': 'Value difference',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1500,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      modal_type: ''
    }
  },
  computed: {
    showControls() {
      return this.$store.state.userGroups.some(group => group.code === 'general_manager' || group.code === 'product_storage_manager' || group.code === 'product_storage_supervisor')
    }
  },
  created() {
    document.title = `Stocktaking (ID: ${ this.$route.params.id }) | Altezza Board`
    this.getStocktaking()
  },
  methods: {
    onPrint() {
      this.$refs.print.preload = true
      getStocktakingPrint(this.$route.params.id)
        .finally(() => { this.$refs.print.preload = false })
        .then(response => { window.open(response.data) })
    },
    onUpdateNotes() {
      patchProductStorageStocktaking(this.$route.params.id, { note: this.stocktaking.note })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
        })
    },
    getStocktaking() {
      getProductStorageStocktakingDetail(this.$route.params.id)
        .then(response => {
          this.stocktaking = response.data
          if (this.stocktaking.status.code === 'completed') {
            this.zero_difference = true
            this.zero_balance = false
          }
          this.getProducts()
        })
    },
    getProducts() {
      getProductStorageStocktakingProducts(
        this.$route.params.id,
        {
          name: this.search,
          hide_zero_balance: this.search ? '' : this.zero_balance,
          hide_zero_difference: this.search ? '' : this.zero_difference
        }
      )
        .then(response => {
          this.products = response.data
          this.is_loaded = true
        })
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    onChangeTableInput(id, real_stock) {
      patchProductStorageStocktakingProduct(this.$route.params.id, id, { real_stock })
        .then(() => {
          this.getStocktaking()
        })
    },
    onDeleteItem() {
      this.$refs.load.preload = true
      deleteProductStorageStocktaking(this.$route.params.id)
        .finally(() => { this.$refs.load.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Stocktaking deleted',
          })
          this.$router.push({name: 'product-storage-stocktaking-list'})
        })
    },
    onUpdateInventory () {
      this.$refs.load.preload = true
      patchProductStorageStocktaking(this.$route.params.id, { status: 'completed' })
        .finally(() => { this.$refs.load.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Stocktaking updated',
          })
          this.getStocktaking()
          this.onHideModal()
        })
    },
    onChangeFilter() {
      this.search = ''
      this.getProducts()
    },
    onShowModal(type) {
      this.modal_type = type
      this.$modal.show('control-modal')
    },
    onHideModal() {
      this.$modal.hide('control-modal')
    },
    onOrdersSearch() {
      this.searchUsers(this)
    },
    searchUsers: debounce(vm => {
      vm.getProducts()
    }, 350),
  }
}
</script>
<style scoped lang="sass">
.stocktaking-block
  margin-bottom: 30px

  &:last-child
    margin-bottom: 0
</style>
